import { useEffect } from "react";
import { observer } from "mobx-react";
import { Box } from "@mui/material";

import AppealsIcon from "../../../icons/AppealsIcon";
import DocumentIcon from "../../../icons/DocumentIcon";
import DollarIcon from "../../../icons/DollarIcon";
import WarningAmberIcon from "../../../icons/WarningAmberIcon";

import { useChatStore } from "../../../../hooks/useChatStore";
import { ExtendedHomeStatisticsCard } from "./ExtendedHomeStatisticsCard";

const formatDollars = (amount: number): string => {
  if (amount >= 1000000) {
    return `$${(amount / 1000000).toFixed(2)}M`;
  }
  if (amount >= 1000) {
    const thousands = (amount / 1000).toFixed(1);
    // toFixed's rounding can push e.g. 999,999.99 to "1000.0" here, which
    // would render as "$1000.0K"; re-render that case as millions instead.
    if (Number(thousands) >= 1000) {
      return `$${(amount / 1000000).toFixed(2)}M`;
    }
    return `$${thousands}K`;
  }
  return `$${amount.toFixed(2)}`;
};

export const ExtendedHomeStatisticsSection = observer(() => {
  const chatStore = useChatStore();
  const {
    dashboardStatistics,
    isDashboardStatisticsLoading,
    hasDashboardStatisticsError,
  } = chatStore;

  useEffect(() => {
    chatStore.loadDashboardStatistics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box
      sx={{
        display: "grid",
        width: "100%",
        gap: 1.5,
        gridTemplateColumns: "1fr",
        "@container home-content (min-width: 480px)": {
          gridTemplateColumns: "repeat(2, 1fr)",
        },
        "@container home-content (min-width: 900px)": {
          gridTemplateColumns: "repeat(4, 1fr)",
        },
      }}
    >
      <ExtendedHomeStatisticsCard
        icon={<DocumentIcon />}
        value={dashboardStatistics?.openCases ?? 0}
        label="Open cases"
        isLoading={isDashboardStatisticsLoading}
        hasError={hasDashboardStatisticsError}
      />

      <ExtendedHomeStatisticsCard
        icon={<WarningAmberIcon />}
        value={dashboardStatistics?.claimsInProgress ?? 0}
        label="Claims in progress"
        isLoading={isDashboardStatisticsLoading}
        hasError={hasDashboardStatisticsError}
      />

      <ExtendedHomeStatisticsCard
        icon={<DollarIcon />}
        value={formatDollars(dashboardStatistics?.dollarsAtRisk ?? 0)}
        label="Dollars at risk"
        isLoading={isDashboardStatisticsLoading}
        hasError={hasDashboardStatisticsError}
      />

      <ExtendedHomeStatisticsCard
        icon={<AppealsIcon />}
        value={dashboardStatistics?.appealsYearToDate ?? 0}
        label="Appeals year to date"
        isLoading={isDashboardStatisticsLoading}
        hasError={hasDashboardStatisticsError}
      />
    </Box>
  );
});

export default ExtendedHomeStatisticsSection;
